<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import FenixLogo from "@/components/FenixLogo.vue";
import { ShieldCheck } from "lucide-vue-next";

const { t } = useI18n();

// Footer nav links. `href` is used for fragment anchors back to the
// landing (prefixed with `/` so they work from any page, not just `/`).
// `routeName` is used for internal router pages (rendered as
// <router-link> so client-side navigation is preserved).
const columns = computed(() => [
  {
    headingKey: "landing.footer.product.heading",
    links: [
      { label: t("landing.footer.product.features"), href: "/#features" },
      { label: t("landing.footer.product.how_it_works"), href: "/#how-it-works" },
      { label: t("landing.footer.product.pricing"), href: "/#pricing" },
    ],
  },
  {
    headingKey: "landing.footer.company.heading",
    links: [
      { label: t("landing.footer.company.about"), routeName: "about-us" },
      { label: t("landing.footer.company.security"), routeName: "security" },
    ],
  },
  {
    headingKey: "landing.footer.contact.heading",
    links: [
      { label: t("landing.footer.contact.sales"), href: "/#contact-form" },
      { label: t("landing.footer.contact.support"), href: "#" },
      { label: t("landing.footer.contact.docs"), href: "#" },
    ],
  },
]);
</script>

<template>
  <footer id="contact" class="bg-secondary/40 border-t border-border scroll-mt-20">
    <div class="container mx-auto px-6 py-12 md:py-14">
      <!-- Mobile layout: brand centered (logo + tagline + compliance badge)
           + 3-column link grid below. Avoids the long vertical stack of
           the desktop grid when it falls back to a single column. -->
      <div class="md:hidden">
        <div class="text-center mb-10">
          <div class="flex justify-center mb-3">
            <FenixLogo size="lg" />
          </div>
          <p class="text-sm text-muted-foreground max-w-[260px] mx-auto leading-relaxed">
            {{ t("landing.footer.tagline") }}
          </p>
          <div class="flex justify-center mt-4">
            <span class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-accent/40 bg-accent/5">
              <ShieldCheck class="w-4 h-4 text-accent" />
              <span class="text-xs font-semibold tracking-wide text-foreground">
                {{ t("landing.footer.compliance.gdpr_badge") }}
              </span>
            </span>
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div
            v-for="col in columns"
            :key="col.headingKey"
          >
            <h4 class="text-[11px] font-semibold uppercase tracking-wider text-foreground mb-3">
              {{ t(col.headingKey) }}
            </h4>
            <ul class="space-y-2">
              <li
                v-for="(link, i) in col.links"
                :key="i"
              >
                <router-link
                  v-if="link.routeName"
                  :to="{ name: link.routeName }"
                  class="text-xs text-muted-foreground hover:text-foreground transition-colors leading-snug block"
                >
                  {{ link.label }}
                </router-link>
                <a
                  v-else
                  :href="link.href"
                  class="text-xs text-muted-foreground hover:text-foreground transition-colors leading-snug block"
                >
                  {{ link.label }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Desktop layout (md+): original 12-col grid -->
      <div class="hidden md:grid md:grid-cols-12 gap-10">
        <!-- Brand column -->
        <div class="md:col-span-5">
          <FenixLogo size="xl" class="mb-4" />
          <p class="text-sm text-muted-foreground max-w-xs">
            {{ t("landing.footer.tagline") }}
          </p>
        </div>

        <!-- Link columns -->
        <div
          v-for="col in columns"
          :key="col.headingKey"
          class="md:col-span-2"
        >
          <h4 class="text-xs font-semibold uppercase tracking-wider text-foreground mb-4">
            {{ t(col.headingKey) }}
          </h4>
          <ul class="space-y-2.5">
            <li
              v-for="(link, i) in col.links"
              :key="i"
            >
              <router-link
                v-if="link.routeName"
                :to="{ name: link.routeName }"
                class="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {{ link.label }}
              </router-link>
              <a
                v-else
                :href="link.href"
                class="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {{ link.label }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Compliance badge -->
        <div class="md:col-span-1">
          <h4 class="text-xs font-semibold uppercase tracking-wider text-foreground mb-4">
            {{ t("landing.footer.compliance.heading") }}
          </h4>
          <span class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-accent/40 bg-accent/5">
            <ShieldCheck class="w-3.5 h-3.5 text-accent flex-shrink-0" />
            <span class="text-[11px] font-semibold tracking-wide text-foreground whitespace-nowrap">
              {{ t("landing.footer.compliance.gdpr_badge") }}
            </span>
          </span>
        </div>
      </div>

      <div class="mt-10 md:mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
        <p class="text-xs text-muted-foreground text-center sm:text-left">
          {{ t("landing.footer.copyright") }}
        </p>
        <div class="flex flex-wrap justify-center gap-x-5 gap-y-2">
          <router-link
            :to="{ name: 'legal-notice' }"
            class="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {{ t("landing.footer.legal.aviso_legal") }}
          </router-link>
          <router-link
            :to="{ name: 'privacy-policy' }"
            class="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {{ t("landing.footer.legal.privacy") }}
          </router-link>
          <router-link
            :to="{ name: 'cookies-policy' }"
            class="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {{ t("landing.footer.legal.cookies") }}
          </router-link>
        </div>
      </div>
    </div>
  </footer>
</template>
